/**
 * External dependencies
 */
import * as React from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  type RefObject,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  Icons,
  Menu,
  PLACEMENT,
  Popup,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import {
  Chip,
  ChipContainer,
  DropDownContainer,
  dropdownStylesOverride,
  SelectedTag,
  SelectedTagLabel,
  SelectedTagClear,
  TagLabel,
} from './components';
import useMedia from '../../../../../app/media/useMedia';
import { SearchInput } from '../../../common';

type Tags = { label: string; group: { label: string; value: string }[] };

const EMPTY_VALUE = '';

export function TagsDropdown({
  disabled,
  initialValue,
  inputRef,
  onChange,
  onSearch,
  setLocalSearchTerm: setParentLocalSearchTerm,
  tags,
}: {
  disabled: boolean;
  initialValue: string;
  inputRef: RefObject<HTMLInputElement>;
  onChange: (value: string) => void;
  onSearch: (value: string) => void;
  setLocalSearchTerm: (value: string) => void;
  tags: Tags;
}) {
  const [localSearchTerm, _setLocalSearchTerm] = useState('');
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);

  const setLocalSearchTerm = useCallback(
    (value) => {
      _setLocalSearchTerm(value);
      setParentLocalSearchTerm(value);
    },
    [setParentLocalSearchTerm]
  );

  const { folder, player, searchTerm, setSearchTerm, tag } = useMedia(
    ({
      media3p: {
        state: { folder, player, searchTerm, tag },
        actions: { setSearchTerm },
      },
    }) => ({
      folder,
      player,
      searchTerm,
      setSearchTerm,
      tag,
    })
  );

  const onTagSelection = useCallback(
    (type, value) => {
      if (type === 'tag' && (value !== tag || folder || player || searchTerm)) {
        setSearchTerm({ tag: value });
      }

      if (
        type === 'folder' &&
        (value !== folder || player || searchTerm || tag)
      ) {
        setSearchTerm({ folder: value });
      }

      if (
        type === 'player' &&
        (value !== player || searchTerm || tag || folder)
      ) {
        setSearchTerm({ player: value });
      }
    },
    [folder, player, setSearchTerm, searchTerm, tag]
  );

  const searchContainerRef = useRef();
  const dropdownContainerRef = useRef();

  const listId = 'search-tags-dropdown';

  const isTagSearch = useMemo(
    () => tags?.label === '' && localSearchTerm,
    [tags?.label, localSearchTerm]
  );

  useEffect(() => {
    if (!folder && !player && !searchTerm && !tag) {
      // Handle resetting the media library
      setLocalSearchTerm(EMPTY_VALUE);
      inputRef.current.setValue(EMPTY_VALUE);
      onSearch(EMPTY_VALUE);
    }
  }, [folder, player, searchTerm, tag]);

  const dropdownOptions = useMemo(() => {
    const options = [tags];

    if (isTagSearch) {
      options.push({
        label: '',
        group: [
          {
            label: `File name contains: “${localSearchTerm}”`,
            value: localSearchTerm,
          },
        ],
      });
    }

    return options;
  }, [isTagSearch, tags]);

  useEffect(() => {
    // We can't just close the dropdown on input blur because the dropdown also includes focusable elements
    const onClickOutside = (event) => {
      const isInsideField =
        searchContainerRef.current?.contains(event.target) ||
        dropdownContainerRef.current?.contains(event.target);

      if (!isInsideField) {
        setShowTagsDropdown(false);
      }
    };

    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, []);

  const RenderDropdownListItem = forwardRef(({ option, ...rest }, ref) => {
    if (option.type) {
      return (
        <li ref={ref} {...rest}>
          <TagLabel
            forwardedAs="span"
            size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
          >
            {option.type.toLowerCase() === 'folder' ? 'Folder: ' : 'Tag: '}
          </TagLabel>
          <ChipContainer>
            <Chip
              forwardedAs="span"
              size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
            >
              {option.label}
            </Chip>
            {/* This count is inaccurate so we're hiding it
            while we wait for clarification from ScorePlay */}
            {/* {isTagSearch && option.itemCount && (
              <TagLabel
                forwardedAs="span"
                isSubLabel
                size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
              >
                {' '}
                in {option.itemCount} media
              </TagLabel>
            )} */}
          </ChipContainer>
        </li>
      );
    }

    return (
      <li ref={ref} {...rest}>
        <Chip
          forwardedAs="span"
          size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
        >
          {option.label}
        </Chip>
      </li>
    );
  });

  const getInputCustomContent = useCallback(() => {
    if (!searchTerm && !player && !tag && !folder) {
      return null;
    }

    let label = '';

    if (searchTerm) {
      label = `File name contains: “${searchTerm}”`;
    } else {
      const item = tags.group.find(
        (item) =>
          item.value === tag || item.value === folder || item.value === player
      );

      label = item?.label || tag || folder || player;
    }

    return (
      <SelectedTag>
        <SelectedTagLabel>{label}</SelectedTagLabel>
        <SelectedTagClear
          onClick={() => {
            if (tag) {
              onTagSelection('tag', EMPTY_VALUE);
            } else if (folder) {
              onTagSelection('folder', EMPTY_VALUE);
            } else if (player) {
              onTagSelection('player', EMPTY_VALUE);
            }

            setLocalSearchTerm(EMPTY_VALUE);
            inputRef.current.setValue(EMPTY_VALUE);
            onSearch(EMPTY_VALUE);
            onChange(EMPTY_VALUE);
          }}
        >
          <Icons.CrossSmall />
        </SelectedTagClear>
      </SelectedTag>
    );
  }, [tags, tag, folder, player, searchTerm]);

  return (
    <>
      <div ref={searchContainerRef}>
        <SearchInput
          delayMs={500}
          initialValue={initialValue}
          value={localSearchTerm}
          incremental
          listId={listId}
          placeholder={__('Search', 'web-stories')}
          onSearch={(value) => {
            setLocalSearchTerm(value);
            onSearch(value);

            // If input was cleared, reset the search results
            if (!value) {
              onChange(value);
            }
          }}
          onFocus={() => setShowTagsDropdown(true)}
          disabled={disabled}
          ref={inputRef}
          customContent={getInputCustomContent()}
        />
      </div>
      <Popup
        anchor={searchContainerRef}
        placement={PLACEMENT.BOTTOM_START}
        isOpen={showTagsDropdown}
        fillWidth
      >
        <DropDownContainer ref={dropdownContainerRef}>
          <Menu
            // Keep focus on the search input
            isMenuFocused={false}
            listId={listId}
            onMenuItemClick={(_event, value, rest) => {
              setShowTagsDropdown(false);

              if (rest.type) {
                onTagSelection(rest.type, value);
                setLocalSearchTerm(EMPTY_VALUE);
                return;
              }

              setLocalSearchTerm(value);
              onChange(value);
            }}
            options={dropdownOptions}
            renderItem={RenderDropdownListItem}
            menuStylesOverride={dropdownStylesOverride}
          />
        </DropDownContainer>
      </Popup>
    </>
  );
}
